import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { API_CONFIG } from '../../config/api.config';
import { prepareCommonHeaders } from '../../services/baseApi';

// Live view of which TS1 base unit each sensor is going through. Comes from
// MQTTConsumer's in-memory responder state (dataAPI /super-admin/sensor-routes):
// current only, no history, and empty for a few minutes after the consumer restarts.

type Company = { id: number; name: string };
type RouteBase = { serial: number; name: string | null; inContact: boolean | null; lastContact: string | null };
type HeardBy = { base: number; rssi: number; samples: number; lastHeardAgoS: number; recent: boolean };
// A copy relayed by repeater(s): `via` is "020001" or "020001>020002" (first hop first),
// rssi is what the base heard from the last repeater
type Relay = HeardBy & { via: string };
type RouteSensor = {
  sensor: number;
  name: string | null;
  responder: number | null;
  responderViaRelay?: boolean;
  lastReadingAgoS: number;
  bases: HeardBy[];
  relays?: Relay[];
};
type Repeater = {
  repeater: string;
  firmware: string | null;
  power: string | null;
  uptimeS: number | null;
  lastSeenAgoS: number;
  online: boolean;
  heardBy: HeardBy[];
};
type RoutesResponse = {
  company: { id: number; name: string; schema: string };
  consumerUptimeS: number;
  bases: RouteBase[];
  sensors: RouteSensor[];
  repeaters?: Repeater[];
};

const REFRESH_MS = 15_000;
const COMPANY_KEY = 'ts1SensorRoutesCompany';
// One colour per base; sensors take the colour of the base answering them
const BASE_COLOURS = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#14b8a6', '#ef4444'];
const UNASSIGNED = -1;
const REPEATER_COLOUR = '#d97706';

function ago(s: number): string {
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
}

// RSSI -> 0..1 signal quality for line weight/opacity (-110 dBm poor, -40 dBm excellent)
function quality(rssi: number): number {
  return Math.min(1, Math.max(0, (rssi + 110) / 70));
}

function readStoredCompany(): number | null {
  try {
    const v = localStorage.getItem(COMPANY_KEY);
    return v ? Number(v) : null;
  } catch {
    return null;
  }
}

export default function SensorRoutesTab() {
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [companyId, setCompanyId] = useState<number | null>(readStoredCompany);
  const [data, setData] = useState<RoutesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_CONFIG.baseURL}/super-admin/customers`, {
          method: 'GET', credentials: 'include', headers: prepareCommonHeaders(),
        });
        if (!res.ok) { setError(`HTTP ${res.status}`); return; }
        const rows: Company[] = await res.json();
        rows.sort((a, b) => a.name.localeCompare(b.name));
        setCompanies(rows);
        setCompanyId(prev => {
          if (prev && rows.some(r => r.id === prev)) return prev;
          return (rows.find(r => r.name.toLowerCase() === 'thermaldev') ?? rows[0])?.id ?? null;
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Request failed');
      }
    })();
  }, []);

  const load = useCallback(async () => {
    if (companyId == null) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_CONFIG.baseURL}/super-admin/sensor-routes?companyId=${companyId}`, {
        method: 'GET', credentials: 'include', headers: prepareCommonHeaders(),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); setError(j.error || `HTTP ${res.status}`); return; }
      setData(await res.json());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    setData(null);
    setSelected(null);
    void load();
    const t = setInterval(() => { void load(); }, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const onCompanyChange = (id: number) => {
    setCompanyId(id);
    try { localStorage.setItem(COMPANY_KEY, String(id)); } catch { /* per-browser convenience only */ }
  };

  const focus = hovered ?? selected;
  const focusSensor = data?.sensors.find(s => s.sensor === focus) ?? null;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-2xl">
          Which TS1 base unit each sensor is going through right now. Every base that hears a sensor publishes its
          readings; the one with the strongest signal answers it (ACKs, time sync, commands). Live only: refreshes every
          15 s, no history.
        </p>
        <div className="flex items-center gap-2">
          <select
            value={companyId ?? ''}
            onChange={e => onCompanyChange(Number(e.target.value))}
            className="text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1.5"
          >
            {companies?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm px-3 py-2">{error}</div>
      )}

      {data && (
        <>
          <Summary data={data} />
          <div className="grid gap-4 2xl:grid-cols-[minmax(0,1fr)_280px]">
            <div className="min-w-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-x-auto">
              <RouteGraph
                data={data}
                focus={focus}
                onHover={setHovered}
                onSelect={s => setSelected(prev => (prev === s ? null : s))}
              />
            </div>
            <DetailPanel sensor={focusSensor} data={data} />
          </div>
          <RepeaterList data={data} companyId={companyId} onChanged={() => void load()} />
          <RouteTable data={data} onSelect={setSelected} selected={selected} />
        </>
      )}

      {!data && !error && (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      )}
    </div>
  );
}

function Summary({ data }: { data: RoutesResponse }) {
  const assigned = data.sensors.filter(s => s.responder !== null).length;
  const multi = data.sensors.filter(s => s.bases.filter(b => b.recent).length > 1).length;
  const warmingUp = data.consumerUptimeS < 300;
  const repeaters = data.repeaters ?? [];
  const stat = (label: string, value: string | number) => (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className="text-xl font-semibold text-gray-900 dark:text-white tabular-nums">{value}</div>
    </div>
  );
  return (
    <>
      {warmingUp && (
        <div className="mb-3 rounded-md bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 text-sm px-3 py-2">
          The MQTT consumer restarted {ago(data.consumerUptimeS)}, so routes are still being learned from incoming readings.
        </div>
      )}
      <div className={`grid grid-cols-2 gap-3 mb-4 ${repeaters.length ? 'sm:grid-cols-5' : 'sm:grid-cols-4'}`}>
        {stat('TS1 base units', data.bases.length)}
        {stat('Sensors heard', data.sensors.length)}
        {stat('Answering base chosen', assigned)}
        {stat('Heard by 2+ bases', multi)}
        {repeaters.length > 0 && stat('Repeaters online', `${repeaters.filter(r => r.online).length} / ${repeaters.length}`)}
      </div>
    </>
  );
}

type Pos = { x: number; y: number };

function RouteGraph({ data, focus, onHover, onSelect }: {
  data: RoutesResponse;
  focus: number | null;
  onHover: (s: number | null) => void;
  onSelect: (s: number) => void;
}) {
  const layout = useMemo(() => {
    // Bases: everything registered, plus any heard base no longer registered
    const baseSerials = data.bases.map(b => b.serial);
    for (const s of data.sensors) for (const h of s.bases) if (!baseSerials.includes(h.base)) baseSerials.push(h.base);
    const groups = new Map<number, RouteSensor[]>(baseSerials.map(b => [b, []]));
    groups.set(UNASSIGNED, []);
    for (const s of data.sensors) {
      const g = s.responder !== null && groups.has(s.responder) ? s.responder : UNASSIGNED;
      groups.get(g)!.push(s);
    }
    const columns = [...baseSerials, ...(groups.get(UNASSIGNED)!.length ? [UNASSIGNED] : [])];

    const colW = 190;
    const perRow = 4;
    const sensorGap = 46;
    const width = Math.max(600, columns.length * colW + 40);
    const cloud: Pos = { x: width / 2, y: 40 };
    const baseY = 150;
    // Repeaters: from beacons, plus any repeater seen on a relayed reading's path
    const repeaterIds = (data.repeaters ?? []).map(r => r.repeater);
    for (const s of data.sensors) for (const r of s.relays ?? []) {
      for (const id of r.via.split('>')) if (!repeaterIds.includes(id)) repeaterIds.push(id);
    }
    const repeaterY = 262;
    const sensorTop = repeaterIds.length ? 372 : 280;
    const repeaterPos = new Map<string, Pos>(
      repeaterIds.map((id, i) => [id, { x: (width * (i + 1)) / (repeaterIds.length + 1), y: repeaterY }])
    );
    const basePos = new Map<number, Pos>();
    const sensorPos = new Map<number, Pos>();
    let maxRows = 1;
    columns.forEach((col, i) => {
      const cx = 20 + colW * i + colW / 2 + (width - 40 - columns.length * colW) / 2;
      if (col !== UNASSIGNED) basePos.set(col, { x: cx, y: baseY });
      const members = groups.get(col)!;
      maxRows = Math.max(maxRows, Math.ceil(members.length / perRow));
      members.forEach((s, j) => {
        const row = Math.floor(j / perRow);
        const inRow = Math.min(perRow, members.length - row * perRow);
        const k = j % perRow;
        sensorPos.set(s.sensor, { x: cx + (k - (inRow - 1) / 2) * sensorGap, y: sensorTop + row * sensorGap });
      });
    });
    const colour = new Map<number, string>(baseSerials.map((b, i) => [b, BASE_COLOURS[i % BASE_COLOURS.length]]));
    const unassignedX = columns.includes(UNASSIGNED) ? 20 + colW * (columns.length - 1) + colW / 2 + (width - 40 - columns.length * colW) / 2 : null;
    return { width, height: sensorTop + maxRows * sensorGap + 20, cloud, basePos, sensorPos, repeaterPos, sensorTop, colour, unassignedX };
  }, [data]);

  const baseInfo = new Map(data.bases.map(b => [b.serial, b]));
  const dim = (s: number) => focus !== null && focus !== s;

  // Curved path from a sensor up to a base
  const curve = (a: Pos, b: Pos) => {
    const my = (a.y + b.y) / 2;
    return `M ${a.x} ${a.y} C ${a.x} ${my}, ${b.x} ${my}, ${b.x} ${b.y}`;
  };

  return (
    <svg
      viewBox={`0 0 ${layout.width} ${layout.height}`}
      width="100%"
      style={{ minWidth: 560 }}
      className="block text-gray-400 dark:text-gray-500"
      role="img"
      aria-label="Sensor to base unit routes"
    >
      <style>{`
        @keyframes routeFlow { to { stroke-dashoffset: -24; } }
        .route-flow { stroke-dasharray: 6 18; animation: routeFlow 1.2s linear infinite; }
        @media (prefers-reduced-motion: reduce) { .route-flow { animation: none; } }
      `}</style>

      {/* Base -> cloud uplinks */}
      {[...layout.basePos.entries()].map(([serial, p]) => {
        const online = baseInfo.get(serial)?.inContact !== false;
        return (
          <g key={`up-${serial}`}>
            <line x1={p.x} y1={p.y - 32} x2={layout.cloud.x} y2={layout.cloud.y + 25}
              stroke="currentColor" strokeOpacity={online ? 0.35 : 0.12} strokeWidth={2} />
            {online && (
              <line x1={p.x} y1={p.y - 32} x2={layout.cloud.x} y2={layout.cloud.y + 25}
                stroke={layout.colour.get(serial)} strokeWidth={2.5} className="route-flow" strokeLinecap="round" />
            )}
          </g>
        );
      })}

      {/* Sensor -> base links: faint for every base that heard it, animated for the one answering it */}
      {data.sensors.map(s => {
        const sp = layout.sensorPos.get(s.sensor);
        if (!sp) return null;
        return s.bases.filter(h => h.recent).map(h => {
          const bp = layout.basePos.get(h.base);
          if (!bp) return null;
          const answering = h.base === s.responder;
          const q = quality(h.rssi);
          const faded = dim(s.sensor);
          const d = curve(sp, { x: bp.x, y: bp.y + 32 });
          return (
            <g key={`l-${s.sensor}-${h.base}`} opacity={faded ? 0.12 : 1}>
              <path d={d} fill="none"
                stroke={answering ? layout.colour.get(h.base) : 'currentColor'}
                strokeOpacity={answering ? 0.55 : 0.08 + 0.25 * q}
                strokeWidth={answering ? 1.5 + 2 * q : 1}
                strokeDasharray={answering ? undefined : '3 4'} />
              {answering && (
                <circle r={4.5} fill={layout.colour.get(h.base)}>
                  <animateMotion dur="2.4s" repeatCount="indefinite" path={d} begin={`${(s.sensor % 12) * 0.2}s`} />
                </circle>
              )}
            </g>
          );
        });
      })}

      {/* Repeater -> base links: from beacons and relayed readings */}
      {(() => {
        const pairs = new Map<string, { id: string; base: number; online: boolean }>();
        for (const r of data.repeaters ?? []) for (const h of r.heardBy) {
          if (h.recent) pairs.set(`${r.repeater}|${h.base}`, { id: r.repeater, base: h.base, online: r.online });
        }
        for (const s of data.sensors) for (const r of s.relays ?? []) {
          if (!r.recent) continue;
          const last = r.via.split('>').pop()!;
          if (!pairs.has(`${last}|${r.base}`)) pairs.set(`${last}|${r.base}`, { id: last, base: r.base, online: true });
        }
        return [...pairs.values()].map(({ id, base, online }) => {
          const rp = layout.repeaterPos.get(id);
          const bp = layout.basePos.get(base);
          if (!rp || !bp) return null;
          return (
            <g key={`rb-${id}-${base}`}>
              <line x1={rp.x} y1={rp.y - 21} x2={bp.x} y2={bp.y + 32}
                stroke={REPEATER_COLOUR} strokeOpacity={online ? 0.3 : 0.12} strokeWidth={1.5} />
              {online && (
                <line x1={rp.x} y1={rp.y - 21} x2={bp.x} y2={bp.y + 32}
                  stroke={REPEATER_COLOUR} strokeOpacity={0.7} strokeWidth={1.5} className="route-flow" strokeLinecap="round" />
              )}
            </g>
          );
        });
      })()}

      {/* Sensor -> repeater hops, and the answering path when it goes through a repeater */}
      {data.sensors.map(s => {
        const sp = layout.sensorPos.get(s.sensor);
        if (!sp) return null;
        const faded = dim(s.sensor);
        const hops = new Set<string>();
        const segs: { key: string; d: string }[] = [];
        for (const r of (s.relays ?? []).filter(x => x.recent)) {
          const ids = r.via.split('>');
          const pts = [sp, ...ids.map(id => layout.repeaterPos.get(id)).filter((x): x is Pos => !!x)];
          for (let i = 0; i + 1 < pts.length; i++) {
            const k = i === 0 ? `${s.sensor}>${ids[0]}` : `${ids[i - 1]}>${ids[i]}`;
            if (hops.has(k)) continue;
            hops.add(k);
            segs.push({ key: k, d: i === 0 ? curve(pts[0], { x: pts[1].x, y: pts[1].y + 21 }) : `M ${pts[i].x} ${pts[i].y} L ${pts[i + 1].x} ${pts[i + 1].y}` });
          }
        }
        const answerRelay = s.responderViaRelay
          ? (s.relays ?? []).filter(r => r.recent && r.base === s.responder).sort((a, b) => b.rssi - a.rssi)[0]
          : undefined;
        // Full sensor -> repeater(s) -> base path of every recent relayed copy, for the moving diamonds
        const relayPaths: { key: string; d: string; answering: boolean }[] = [];
        for (const r of (s.relays ?? []).filter(x => x.recent)) {
          const pts = r.via.split('>').map(id => layout.repeaterPos.get(id)).filter((x): x is Pos => !!x);
          const bp = layout.basePos.get(r.base);
          if (!pts.length || !bp) continue;
          relayPaths.push({
            key: `${r.base}|${r.via}`,
            d: `M ${sp.x} ${sp.y} ` + pts.map(p => `L ${p.x} ${p.y}`).join(' ') + ` L ${bp.x} ${bp.y + 32}`,
            answering: r === answerRelay,
          });
        }
        return (
          <g key={`rl-${s.sensor}`} opacity={faded ? 0.12 : 1}>
            {segs.map(sg => (
              <g key={sg.key}>
                <path d={sg.d} fill="none" stroke={REPEATER_COLOUR} strokeOpacity={0.35} strokeWidth={2.5} />
                <path d={sg.d} fill="none" stroke={REPEATER_COLOUR} strokeOpacity={0.9} strokeWidth={2.5}
                  className="route-flow" strokeLinecap="round" />
              </g>
            ))}
            {relayPaths.map(rp => (
              <rect key={rp.key} x={-5} y={-5} width={10} height={10} transform="rotate(45)"
                fill={rp.answering && s.responder !== null ? layout.colour.get(s.responder) : REPEATER_COLOUR}
                stroke="white" strokeWidth={1.5} className="dark:stroke-gray-800">
                <animateMotion dur="3.2s" repeatCount="indefinite" path={rp.d} begin={`${(s.sensor % 12) * 0.2}s`} />
              </rect>
            ))}
          </g>
        );
      })}

      {/* Repeaters */}
      {[...layout.repeaterPos.entries()].map(([id, p]) => {
        const info = (data.repeaters ?? []).find(r => r.repeater === id);
        const online = info ? info.online : null;
        return (
          <g key={`r-${id}`}>
            <rect x={p.x - 20} y={p.y - 20} width={40} height={40} rx={5} transform={`rotate(45 ${p.x} ${p.y})`}
              fill={online === false ? '#9ca3af' : REPEATER_COLOUR} fillOpacity={0.15}
              stroke={online === false ? '#9ca3af' : REPEATER_COLOUR} strokeWidth={2} />
            <text x={p.x} y={p.y + 5} textAnchor="middle" className="fill-gray-900 dark:fill-gray-100 text-[13px] font-semibold">R</text>
            <text x={p.x + 36} y={p.y - 3} paintOrder="stroke" strokeWidth={4} strokeLinejoin="round"
              className="fill-gray-900 stroke-white dark:fill-gray-100 dark:stroke-gray-800 text-[14px] font-semibold tabular-nums">{id}</text>
            <text x={p.x + 36} y={p.y + 14} paintOrder="stroke" strokeWidth={4} strokeLinejoin="round"
              className="fill-gray-500 stroke-white dark:fill-gray-400 dark:stroke-gray-800 text-[12px]">
              {online === null ? 'no beacon yet' : online ? `repeater · ${info?.power ?? ''}` : 'offline'}
            </text>
          </g>
        );
      })}

      {/* Cloud */}
      <g>
        <rect x={layout.cloud.x - 90} y={layout.cloud.y - 25} width={180} height={50} rx={25}
          className="fill-indigo-50 stroke-indigo-300 dark:fill-indigo-950 dark:stroke-indigo-700" strokeWidth={1.5} />
        <text x={layout.cloud.x} y={layout.cloud.y + 6} textAnchor="middle"
          className="fill-indigo-700 dark:fill-indigo-300 text-[17px] font-semibold">TS1 Cloud</text>
      </g>

      {/* Bases */}
      {[...layout.basePos.entries()].map(([serial, p]) => {
        const info = baseInfo.get(serial);
        const online = info?.inContact !== false;
        const answering = data.sensors.filter(s => s.responder === serial).length;
        return (
          <g key={`b-${serial}`}>
            <rect x={p.x - 82} y={p.y - 32} width={164} height={64} rx={10}
              fill={online ? layout.colour.get(serial) : '#9ca3af'} fillOpacity={0.12}
              stroke={online ? layout.colour.get(serial) : '#9ca3af'} strokeWidth={2} />
            <text x={p.x} y={p.y - 10} textAnchor="middle" className="fill-gray-900 dark:fill-gray-100 text-[16px] font-semibold">
              {serial}
            </text>
            <text x={p.x} y={p.y + 7} textAnchor="middle" className="fill-gray-600 dark:fill-gray-300 text-[13px]">
              {info?.name ? (info.name.length > 20 ? `${info.name.slice(0, 19)}…` : info.name) : ' '}
            </text>
            <text x={p.x} y={p.y + 23} textAnchor="middle" className="fill-gray-500 dark:fill-gray-400 text-[13px]">
              {!info ? 'not registered' : !online ? 'offline' : `answers ${answering}`}
            </text>
          </g>
        );
      })}
      {layout.unassignedX !== null && (
        <text x={layout.unassignedX} y={layout.sensorTop - 44} textAnchor="middle" className="fill-gray-500 dark:fill-gray-400 text-[13px]">
          Not assigned yet (every base answers)
        </text>
      )}

      {/* Sensors */}
      {data.sensors.map(s => {
        const p = layout.sensorPos.get(s.sensor);
        if (!p) return null;
        const fill = s.responder !== null ? layout.colour.get(s.responder) ?? '#9ca3af' : '#9ca3af';
        const fresh = s.lastReadingAgoS < 20;
        const relayVia = [...new Set((s.relays ?? []).filter(r => r.recent).map(r => r.via.replace(/>/g, ' → ')))];
        const relayed = relayVia.length > 0;
        return (
          <g key={`s-${s.sensor}`} className="cursor-pointer" opacity={dim(s.sensor) ? 0.3 : 1}
            onMouseEnter={() => onHover(s.sensor)} onMouseLeave={() => onHover(null)}
            onClick={() => onSelect(s.sensor)}>
            {fresh && (
              <circle cx={p.x} cy={p.y} r={17} fill="none" stroke={fill} strokeWidth={2}>
                <animate attributeName="r" from="14" to="27" dur="1.5s" repeatCount="indefinite" />
                <animate attributeName="opacity" from="0.8" to="0" dur="1.5s" repeatCount="indefinite" />
              </circle>
            )}
            {relayed && (
              <circle cx={p.x} cy={p.y} r={20.5} fill="none" stroke={REPEATER_COLOUR} strokeWidth={2.5} strokeDasharray="5 3" />
            )}
            <circle cx={p.x} cy={p.y} r={focus === s.sensor ? 17 : 15} fill={fill}
              stroke="white" strokeWidth={2} className="dark:stroke-gray-800" />
            {relayed && (
              <g>
                <rect x={p.x + 7} y={p.y - 21} width={14} height={14} rx={2} transform={`rotate(45 ${p.x + 14} ${p.y - 14})`}
                  fill={REPEATER_COLOUR} stroke="white" strokeWidth={1.5} className="dark:stroke-gray-800" />
                <text x={p.x + 14} y={p.y - 10} textAnchor="middle" className="fill-white text-[10px] font-bold">R</text>
              </g>
            )}
            <text x={p.x} y={p.y + 31} textAnchor="middle" className="fill-gray-600 dark:fill-gray-300 text-[12px] tabular-nums">
              {s.sensor}
            </text>
            <title>{`${s.name ?? s.sensor} — ${s.responder !== null ? `answered by ${s.responder}` : 'no answering base yet'}${relayed ? ` · relayed by ${relayVia.join(', ')}` : ''}`}</title>
          </g>
        );
      })}
    </svg>
  );
}

function DetailPanel({ sensor, data }: { sensor: RouteSensor | null; data: RoutesResponse }) {
  const baseName = (serial: number) => data.bases.find(b => b.serial === serial)?.name;
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 text-sm">
      {!sensor ? (
        <div className="text-gray-500 dark:text-gray-400">
          <p className="font-medium text-gray-700 dark:text-gray-200 mb-2">How to read this</p>
          <ul className="space-y-1.5 list-disc pl-4">
            <li>Solid line with moving dot: the base answering the sensor.</li>
            <li>Dashed lines: other bases that also hear it (darker = stronger signal).</li>
            <li>Pulsing ring: a reading arrived in the last 20 s.</li>
            <li>Diamonds: repeaters. A sensor with an orange ring and an “R” badge is being relayed: follow the flowing orange line and moving diamond through the repeater to the base.</li>
            <li>Hover or click a sensor for its signal at each base.</li>
          </ul>
        </div>
      ) : (
        <>
          <p className="font-semibold text-gray-900 dark:text-white">{sensor.name ?? `Sensor ${sensor.sensor}`}</p>
          <p className="text-gray-500 dark:text-gray-400 mb-3">
            {sensor.sensor} · last reading {ago(sensor.lastReadingAgoS)}
          </p>
          <p className="mb-2 text-gray-700 dark:text-gray-200">
            {sensor.responder !== null
              ? <>Answered by <span className="font-semibold">{sensor.responder}</span>{baseName(sensor.responder) ? ` (${baseName(sensor.responder)})` : ''}{sensor.responderViaRelay ? ' through a repeater (no base hears it directly)' : ''}</>
              : 'No answering base chosen yet: every base answers it.'}
          </p>
          {(sensor.relays ?? []).length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">Relayed by repeater</p>
              {sensor.relays!.map(r => (
                <div key={`${r.base}|${r.via}`} className={`flex justify-between text-xs ${r.recent ? 'text-gray-600 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}`}>
                  <span>{r.via.replace(/>/g, ' → ')} → {r.base}</span>
                  <span className="tabular-nums">{r.rssi} dBm · {ago(r.lastHeardAgoS)}</span>
                </div>
              ))}
            </div>
          )}
          {sensor.bases.length > 0 && <p className="text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">Heard directly</p>}
          <div className="space-y-2">
            {sensor.bases.map(h => (
              <div key={h.base}>
                <div className="flex justify-between text-xs">
                  <span className={h.base === sensor.responder ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'}>
                    {h.base}{h.base === sensor.responder ? ' · answering' : ''}{!h.recent ? ' · stopped hearing it' : ''}
                  </span>
                  <span className="tabular-nums text-gray-500 dark:text-gray-400">{h.rssi} dBm</span>
                </div>
                <div className="h-1.5 rounded bg-gray-100 dark:bg-gray-700 overflow-hidden">
                  <div className={`h-full rounded ${h.base === sensor.responder ? 'bg-indigo-500' : 'bg-gray-400 dark:bg-gray-500'}`}
                    style={{ width: `${Math.round(quality(h.rssi) * 100)}%`, opacity: h.recent ? 1 : 0.4 }} />
                </div>
                <div className="text-[11px] text-gray-400 dark:text-gray-500">heard {ago(h.lastHeardAgoS)} · {h.samples} readings</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function RepeaterList({ data, companyId, onChanged }: { data: RoutesResponse; companyId: number | null; onChanged: () => void }) {
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const repeaters = data.repeaters ?? [];
  // Ids seen only on relayed readings' paths (no beacon), listed so they can be removed too
  const pathOnly: string[] = [];
  for (const s of data.sensors) for (const r of s.relays ?? []) for (const id of r.via.split('>')) {
    if (!repeaters.some(b => b.repeater === id) && !pathOnly.includes(id)) pathOnly.push(id);
  }
  if (repeaters.length === 0 && pathOnly.length === 0) return null;

  const remove = async (id: string) => {
    if (companyId == null) return;
    setBusy(id);
    setError(null);
    try {
      const res = await fetch(`${API_CONFIG.baseURL}/super-admin/sensor-routes/forget-repeater`, {
        method: 'POST', credentials: 'include',
        headers: { ...prepareCommonHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId, repeater: id }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); setError(j.error || `HTTP ${res.status}`); return; }
      setConfirming(null);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setBusy(null);
    }
  };

  const removeCell = (id: string) => (
    <td className="px-3 py-2 text-right whitespace-nowrap">
      {confirming === id ? (
        <span className="inline-flex items-center gap-2">
          <button type="button" disabled={busy === id} onClick={() => void remove(id)}
            className="px-2 py-0.5 text-xs rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
            {busy === id ? 'Removing…' : 'Confirm remove'}
          </button>
          <button type="button" onClick={() => setConfirming(null)}
            className="px-2 py-0.5 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200">
            Cancel
          </button>
        </span>
      ) : (
        <button type="button" onClick={() => setConfirming(id)}
          className="px-2 py-0.5 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
          Remove
        </button>
      )}
    </td>
  );

  return (
    <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
      {error && <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm px-3 py-2">{error}</div>}
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs text-gray-500 dark:text-gray-400">
          <tr>
            <th className="px-3 py-2 font-medium">Repeater</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Firmware</th>
            <th className="px-3 py-2 font-medium">Power</th>
            <th className="px-3 py-2 font-medium">Up for</th>
            <th className="px-3 py-2 font-medium">Last beacon</th>
            <th className="px-3 py-2 font-medium">Heard by</th>
            <th className="px-3 py-2 font-medium text-right"
              title="Clears it from this live view. A real repeater comes back with its next beacon (every 5 min).">
              Remove
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-900">
          {repeaters.map(r => (
            <tr key={r.repeater}>
              <td className="px-3 py-2 tabular-nums font-medium text-gray-900 dark:text-gray-100">{r.repeater}</td>
              <td className="px-3 py-2">
                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${r.online
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
                  {r.online ? 'Online' : 'Offline'}
                </span>
              </td>
              <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{r.firmware ?? '—'}</td>
              <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{r.power === 'BAT' ? 'Battery' : r.power ?? '—'}</td>
              <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{r.uptimeS != null ? ago(r.uptimeS).replace(' ago', '') : '—'}</td>
              <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{ago(r.lastSeenAgoS)}</td>
              <td className="px-3 py-2 tabular-nums text-gray-600 dark:text-gray-400">
                {r.heardBy.map(h => `${h.base} (${h.rssi})`).join(', ') || '—'}
              </td>
              {removeCell(r.repeater)}
            </tr>
          ))}
          {pathOnly.map(id => (
            <tr key={`p-${id}`}>
              <td className="px-3 py-2 tabular-nums font-medium text-gray-900 dark:text-gray-100">{id}</td>
              <td className="px-3 py-2">
                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                  No beacon
                </span>
              </td>
              <td colSpan={5} className="px-3 py-2 text-gray-500 dark:text-gray-400">Only seen on relayed readings</td>
              {removeCell(id)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RouteTable({ data, onSelect, selected }: { data: RoutesResponse; onSelect: (s: number) => void; selected: number | null }) {
  if (data.sensors.length === 0) {
    return (
      <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
        No TS1 sensor readings seen since the MQTT consumer started.
      </p>
    );
  }
  return (
    <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs text-gray-500 dark:text-gray-400">
          <tr>
            <th className="px-3 py-2 font-medium">Sensor</th>
            <th className="px-3 py-2 font-medium">Name</th>
            <th className="px-3 py-2 font-medium">Answered by</th>
            <th className="px-3 py-2 font-medium">Also heard by</th>
            <th className="px-3 py-2 font-medium">Via repeater</th>
            <th className="px-3 py-2 font-medium">Last reading</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-900">
          {data.sensors.map(s => {
            const answering = s.bases.find(b => b.base === s.responder);
            const others = s.bases.filter(b => b.base !== s.responder && b.recent);
            return (
              <tr key={s.sensor} onClick={() => onSelect(s.sensor)}
                className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${selected === s.sensor ? 'bg-indigo-50 dark:bg-indigo-950/40' : ''}`}>
                <td className="px-3 py-2 tabular-nums text-gray-900 dark:text-gray-100">{s.sensor}</td>
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{s.name ?? '—'}</td>
                <td className="px-3 py-2 tabular-nums text-gray-900 dark:text-gray-100">
                  {s.responder ?? 'not chosen yet'}{answering ? <span className="text-gray-500 dark:text-gray-400"> ({answering.rssi} dBm)</span> : null}{s.responderViaRelay ? <span className="text-amber-600 dark:text-amber-400"> via repeater</span> : null}
                </td>
                <td className="px-3 py-2 tabular-nums text-gray-600 dark:text-gray-400">
                  {others.length ? others.map(o => `${o.base} (${o.rssi})`).join(', ') : '—'}
                </td>
                <td className="px-3 py-2 tabular-nums text-gray-600 dark:text-gray-400">
                  {[...new Set((s.relays ?? []).filter(r => r.recent).map(r => r.via.replace(/>/g, ' → ')))].join(', ') || '—'}
                </td>
                <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{ago(s.lastReadingAgoS)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
